import { gql } from "@/graphql/typed-gql"
import { DISCUSSION_FIELDS } from "@/graphql/mutations/sessions"

export const GET_SESSIONS = gql`
  query GetSessions($studentId: ID, $subjectKey: String) {
    sessions(studentId: $studentId, subjectKey: $subjectKey) {
      id
      status
      createdAt
      completedAt
      activeSeconds
      subjectKey
      subject {
        id
        name
        icon
      }
      student {
        id
        name
        avatarUrl
      }
      exercises {
        id
        skillKey
        result
      }
    }
  }
`

export const GET_SESSION = gql`
  query GetSession($id: ID!) {
    session(id: $id) {
      id
      status
      createdAt
      completedAt
      activeSeconds
      pointsEarned
      pointsRules {
        completion
        correctAnswer
        partialAnswer
        perfectSession
      }
      # Whether an unresolved conversation blocks finishing for this learner.
      # Off unless a grown-up switched it on — see DiscussionGate.
      requiresDiscussionResolution
      gamificationBreakdown {
        type
        points
        message
        skillName
        achievementName
        achievementIcon
        levelName
        levelIcon
      }
      customConfig
      personalizationStatus
      personalizedProblemCount
      personalizationProfile
      student {
        id
        name
        grade
        suggestedExerciseCount
        # „Dar viena sesija" prints the length it is about to ask for, so it has
        # to know this subject's ceiling — otherwise it promises the length the
        # child chose and the server serves the shorter one.
        subjectSessionLengths {
          subjectKey
          count
        }
      }
      subjectKey
      subject {
        id
        name
        icon
      }
      exercises {
        id
        title
        instructions
        theory
        concepts
        category
        skillKey
        categoryDisplay {
          label
          labelShort
          emoji
          color
          bgColor
          borderColor
          description
        }
        problemText
        expectedAnswer
        hint
        reveal
        choices
        alternativeAnswers
        answerType
        answerRules {
          caseSensitive
          ignoreDiacritics
          exactForm
          form
        }
        timeLimit
        result
        userAnswer
        answerRevealed
        manuallyGraded
        recheckRequestedAt
        reportedAt
        theoryOpenedAt
        isIntroduction
        introductionKind
        activeSeconds
        answerSeconds
        answeredLate
        skillKey
        skill {
          name
        }
        discussion {
          ${DISCUSSION_FIELDS}
        }
      }
      skillChanges {
        skillKey
        skillName
        oldProficiency
        newProficiency
        change
      }
      sessionPlan {
        skillKey
        category
        exerciseCount
        skillName
        categoryDisplay {
          label
          labelShort
          emoji
          color
          bgColor
          borderColor
          description
        }
      }
      hasNewSkill
      newSkillKey
    }
  }
`

export const GET_SESSION_FOR_PRINT = gql`
  query GetSessionForPrint($id: ID!) {
    sessionForPrint(id: $id) {
      id
      createdAt
      student {
        name
      }
      exercises {
        id
        skillKey
        title
        instructions
        theory
        concepts
        problemText
        expectedAnswer
        hint
        choices
        alternativeAnswers
        answerType
        answerRules {
          caseSensitive
          ignoreDiacritics
          exactForm
          form
        }
        skill {
          name
        }
      }
    }
  }
`

// What a session of a given length would come out as, before generating it.
// The planner caps mean a chosen 20 can become a 15, and this is what says so
// while the choice is still being made.
export const SESSION_PLAN_PREVIEW = gql`
  query SessionPlanPreview(
    $sessionId: ID!
    $targetExerciseCount: Int
    $maxNewSkills: Int
    $includeDistantReview: Boolean
  ) {
    sessionPlanPreview(
      sessionId: $sessionId
      targetExerciseCount: $targetExerciseCount
      maxNewSkills: $maxNewSkills
      includeDistantReview: $includeDistantReview
    ) {
      requestedCount
      plannedCount
      limits {
        code
        message
        detail
        option
        suggestedValue
        lost
      }
      skills {
        skillKey
        skillName
        category
        level
        exerciseCount
      }
    }
  }
`
